/* Campaign videos play only while on screen, in the active slide, without reduced motion and not on a phone saving data. They are not autoplay: this starts them, so the page and its poster come first. */
/* Runs once per page, however many slideshow sections include it: a second copy finds the element defined and skips, so the video watchdog is never doubled. */
if (!customElements.get('c4-slideshow')) {
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  /* A phone saving data, asking for less, or on a slow connection keeps the poster: the video never downloads. */
  const connection = navigator.connection || {};
  const lite =
    matchMedia('(max-width: 599px)').matches &&
    (connection.saveData ||
      /(^|-)2g|3g/.test(connection.effectiveType || '') ||
      matchMedia('(prefers-reduced-data: reduce)').matches);
  const onScreen = new WeakSet();
  function tend(video) {
    const slide = video.closest('[data-slide]');
    const shown = !slide?.closest('c4-slideshow[data-ready]') || slide.hasAttribute('data-active');
    if (shown && onScreen.has(video) && !motion.matches && !lite) video.play().catch(() => {});
    else video.pause();
  }
  const videos = document.querySelectorAll('.campaign video');
  if (videos.length) {
    const watcher = new IntersectionObserver(
      (entries) =>
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) onScreen.add(target);
          else onScreen.delete(target);
          tend(target);
        }),
      { threshold: 0.25 },
    );
    videos.forEach((video) => {
      watcher.observe(video);
      /* A seamless loop: back to the start just before the end, so the browser never reaches "ended" (where Safari,
         streaming above all, drops the picture for a moment before the loop restarts). */
      /* Fades in over its still once it is really playing (campaign-video.css). */
      video.addEventListener('playing', () => video.setAttribute('data-playing', ''), {
        once: true,
      });
      video.addEventListener('timeupdate', () => {
        if (video.duration > 1 && video.duration - video.currentTime < 0.3) video.currentTime = 0;
      });
      /* Belt and braces for looping: a video that reaches its end anyway starts again, and one that stalled picks up once it can play. */
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        tend(video);
      });
      video.addEventListener('canplay', () => tend(video));
    });
    /*
    A watchdog for a video the browser has let go: once a second, one that should be playing but is paused (a
    browser pausing it on its own, or after a tab switch) is played again. A video that is only buffering is left to
    buffer: jumping ahead of what has loaded only asks for more and skips the film. Only one that has not moved for
    three seconds with plenty already loaded (a file its decoder will not get past) is stepped forward.
  */
    const frozen = new WeakMap();
    setInterval(() => {
      if (document.hidden || motion.matches || lite) return;
      videos.forEach((video) => {
        const slide = video.closest('[data-slide]');
        const shown =
          !slide?.closest('c4-slideshow[data-ready]') || slide.hasAttribute('data-active');
        if (!shown || !onScreen.has(video)) return frozen.delete(video);
        if (video.paused) {
          video.play().catch(() => {});
          return frozen.delete(video);
        }
        const [time, count] = frozen.get(video) || [-1, 0];
        const stuck = video.currentTime === time ? count + 1 : 0;
        frozen.set(video, [video.currentTime, stuck]);
        const loaded = video.buffered.length
          ? video.buffered.end(video.buffered.length - 1) - video.currentTime
          : 0;
        if (stuck >= 3 && video.readyState === 4 && loaded > 1) {
          video.currentTime = (video.currentTime + 0.25) % (video.duration || 1);
          frozen.delete(video);
        }
      });
    }, 1000);
    motion.addEventListener('change', () => videos.forEach(tend));
  }
  /* Slideshows sharing data-sync (the columns of one section) advance on one clock. */
  function peers(slideshow) {
    const { sync } = slideshow.dataset;
    if (!sync) return [slideshow];
    return [...document.querySelectorAll('c4-slideshow[data-sync]')].filter(
      (other) => other.dataset.sync === sync && other.slides?.length,
    );
  }
  class C4Slideshow extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.links = [...this.querySelectorAll('.campaign-links a')];
      this.status = this.querySelector('[data-slide-status]');
      this.motion = motion;
      this.paused =
        this.dataset.autoplay !== 'true' ||
        this.motion.matches ||
        Boolean(window.Shopify?.designMode);
      this.index = 0;
      this.visible = false;
      this.hovered = false;
      /* The first banner is the one showing before the script: active before the rest wait their turn, so its words
         carry on coming in rather than starting again (campaign-copy.css). */
      this.slides[0].setAttribute('data-active', '');
      this.setAttribute('data-ready', '');
      this.show(0, false);
      this.querySelectorAll('.campaign').forEach((panel) => this.setContrast(panel));
      this.querySelectorAll('button[hidden]').forEach((button) => {
        button.hidden = false;
      });
      this.links.forEach((link, index) =>
        link.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.paused = true;
            this.show(index);
          },
          options,
        ),
      );
      this.querySelectorAll('[data-slide-step]').forEach((button) =>
        button.addEventListener(
          'click',
          () => {
            this.paused = true;
            this.show(this.index + Number(button.dataset.slideStep));
          },
          options,
        ),
      );
      this.addEventListener(
        'focusin',
        () => {
          this.paused = true;
          this.schedule();
        },
        options,
      );
      this.addEventListener(
        'pointerenter',
        () => {
          this.hovered = true;
          this.schedule();
        },
        options,
      );
      this.addEventListener(
        'pointerleave',
        () => {
          this.hovered = false;
          this.schedule();
        },
        options,
      );
      this.motion.addEventListener(
        'change',
        () => {
          this.paused = true;
          this.slides.forEach((slide) =>
            slide.getAnimations({ subtree: true }).forEach((animation) => animation.cancel()),
          );
          this.show(this.index, false);
        },
        options,
      );
      const track = this.querySelector('.campaign-track');
      track.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'touch') this.touchStart = [event.clientX, event.clientY];
        },
        options,
      );
      track.addEventListener(
        'pointerup',
        (event) => {
          if (!this.touchStart) return;
          const [x, y] = this.touchStart;
          this.touchStart = null;
          const distance = event.clientX - x;
          if (Math.abs(distance) > 50 && Math.abs(distance) > Math.abs(event.clientY - y)) {
            this.paused = true;
            this.show(this.index + (distance < 0 ? 1 : -1));
          }
        },
        options,
      );
      track.addEventListener(
        'pointercancel',
        () => {
          this.touchStart = null;
        },
        options,
      );
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.slides.indexOf(event.target.closest('[data-slide]'));
          if (index >= 0) {
            this.paused = true;
            this.show(index, false);
          }
        },
        options,
      );
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.schedule();
        },
        { threshold: 0.25 },
      );
      this.observer.observe(this);
      this.schedule();
    }
    /* Slides moved in or out (the hero's columns merging for a narrower window): start again with what's there. */
    refresh() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.started = false;
      this.removeAttribute('data-ready');
      this.connectedCallback();
    }
    /*
      Shows a slide. Picked by a person (paused) in a hero whose columns rotate together (data-together), the other
      columns show the same set, so paired panels stay paired; each announces the change (slideshow:change).
    */
    show(index, animate = true, follow = true) {
      const next = (index + this.slides.length) % this.slides.length;
      const previous = this.slides[this.index];
      this.slides.forEach((slide, i) => {
        slide
          .getAnimations({ subtree: true })
          .filter((animation) => !(animation instanceof CSSAnimation))
          .forEach((animation) => animation.cancel());
        slide.toggleAttribute('data-active', i === next);
        slide.inert = i !== next;
        slide.setAttribute('aria-hidden', String(i !== next));
      });
      this.links.forEach((link, i) => {
        if (i === next) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const active = this.slides[next];
      if (animate && next !== this.index && !this.motion.matches) {
        previous.setAttribute('data-active', '');
        const exit = previous.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240 });
        exit.onfinish = () => {
          if (previous !== this.slides[this.index]) previous.removeAttribute('data-active');
        };
        active.animate(
          [
            { opacity: 0, transform: 'scale(.97)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          {
            duration: 550,
            easing: 'cubic-bezier(.3,1.25,.4,1)',
          },
        );
      }
      this.index = next;
      this.querySelectorAll('video').forEach(tend);
      const nextImage = this.slides[(next + 1) % this.slides.length].querySelector('img');
      if (nextImage) nextImage.loading = 'eager';
      if (animate && this.paused)
        this.status.textContent = `${next + 1} / ${this.slides.length}: ${active.querySelector('h1,h2')?.textContent || ''}`;
      this.dispatchEvent(
        new CustomEvent('slideshow:change', { bubbles: true, detail: { index: next } }),
      );
      if (follow && this.paused && this.dataset.together === 'true')
        peers(this)
          .filter((peer) => peer !== this)
          .forEach((peer) => {
            peer.paused = true;
            peer.show(index, animate, false);
          });
      this.schedule();
    }
    schedule() {
      const group = peers(this);
      const lead = group[0];
      clearTimeout(lead.timer);
      /* the hairline over the link (or the ring round the dot, hero-columns.css) is the time-to-next: it runs only
         while rotation is actually scheduled */
      group.forEach((member) => {
        member.removeAttribute('data-running');
        member.slides.forEach((slide) => slide.removeAttribute('data-running'));
      });
      if (
        document.hidden ||
        group.every((member) => member.slides.length < 2) ||
        group.some((member) => member.paused || member.hovered || !member.visible)
      )
        return;
      /* A staggered column (data-delay) waits its share of a beat the first time, so columns turn in turn. */
      const delay = lead.started ? 0 : Number(lead.dataset.delay) || 0;
      lead.started = true;
      const wait = Math.max(5000, Number(lead.dataset.interval) || 7000) + delay;
      group.forEach((member) => {
        const active = member.slides[member.index];
        member.style.setProperty('--slide-interval', `${wait}ms`);
        void active.offsetWidth;
        active.setAttribute('data-running', '');
        member.setAttribute('data-running', '');
      });
      lead.timer = setTimeout(() => group.forEach((member) => member.show(member.index + 1)), wait);
    }
    setContrast(slide) {
      if (slide.dataset.contrast !== 'auto') return;
      const image = slide.querySelector('img');
      if (!image) return;
      const sample = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 8;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, 8, 8);
          const pixels = context.getImageData(0, 0, 8, 8).data;
          let brightness = 0;
          for (let i = 0; i < pixels.length; i += 4)
            brightness += 0.2126 * pixels[i] + 0.7152 * pixels[i + 1] + 0.0722 * pixels[i + 2];
          slide.dataset.tone = brightness / 64 > 150 ? 'light' : 'dark';
        } catch {
          /* Cross-origin or unavailable images retain protected dark glass. */
        }
      };
      if (image.complete && image.naturalWidth) sample();
      else image.addEventListener('load', sample, { once: true, signal: this.abort.signal });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.slides?.forEach((slide) =>
        slide.getAnimations({ subtree: true }).forEach((animation) => animation.cancel()),
      );
      this.slides = null;
    }
  }
  if (!customElements.get('c4-slideshow')) customElements.define('c4-slideshow', C4Slideshow);
}
